import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer'
import type { Resume, ResumeSectionKey } from '../../../types'
import {
  contactInline,
  joinDateRange,
  getSectionOrder,
  labelFor,
  displayUrl,
  BULLET,
  pdfMetadata,
  pdfFontFamily,
  previewFontFamily,
  type PdfFontFamily,
} from './shared'

const TEXT = '#0f172a'
const MUTED = '#475569'

const makeStyles = (font: PdfFontFamily) =>
  StyleSheet.create({
    page: { paddingTop: 28, paddingBottom: 38, paddingHorizontal: 32, fontSize: 9.5, color: TEXT, fontFamily: font.regular },
    headerName: { fontSize: 16, fontFamily: font.bold },
    headerLine: { fontSize: 9, color: MUTED, marginTop: 1 },
    sectionTitle: { fontSize: 9.5, fontFamily: font.bold, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 8, marginBottom: 3, color: TEXT },
    itemHeader: { flexDirection: 'row', justifyContent: 'space-between' },
    itemTitle: { fontSize: 9.5, fontFamily: font.bold },
    itemMeta: { fontSize: 8.5, color: MUTED },
    bulletRow: { flexDirection: 'row' },
    bulletDot: { width: 8, fontSize: 9.5 },
    bulletText: { flex: 1, fontSize: 9.5, lineHeight: 1.3 },
    paragraph: { fontSize: 9.5, lineHeight: 1.35 },
    pageNumber: { position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', fontSize: 8, color: MUTED },
    linkInline: { textDecoration: 'none', color: '#1d4ed8', fontSize: 8.5 },
  })

type CompactStyles = ReturnType<typeof makeStyles>

function renderSection(key: ResumeSectionKey, resume: Resume, styles: CompactStyles) {
  switch (key) {
    case 'summary':
      if (!resume.summary) return null
      return (
        <View key={key} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'summary')}</Text>
          <Text style={styles.paragraph}>{resume.summary}</Text>
        </View>
      )
    case 'experience':
      if (resume.experience.length === 0) return null
      return (
        <View key={key}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'experience')}</Text>
          {resume.experience.map((exp) => (
            <View key={exp.id} style={{ marginBottom: 4 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{exp.role}{exp.company ? ` · ${exp.company}` : ''}</Text>
                <Text style={styles.itemMeta}>{joinDateRange(exp.startDate, exp.endDate)}</Text>
              </View>
              {exp.location ? <Text style={styles.itemMeta}>{exp.location}</Text> : null}
              {exp.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'skills':
      if (resume.skills.length === 0) return null
      return (
        <View key={key} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'skills')}</Text>
          {resume.skills.map((g) => (
            <Text key={g.id} style={styles.paragraph}>
              <Text style={{ fontWeight: 700 }}>{g.category}: </Text>
              {g.items.join(', ')}
            </Text>
          ))}
        </View>
      )
    case 'education':
      if (resume.education.length === 0) return null
      return (
        <View key={key}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'education')}</Text>
          {resume.education.map((e) => (
            <View key={e.id} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>
                  {[e.degree, e.field].filter(Boolean).join(', ') || e.institution}
                </Text>
                <Text style={styles.itemMeta}>{joinDateRange(e.startDate, e.endDate)}</Text>
              </View>
              <Text style={styles.itemMeta}>{[e.institution, e.location].filter(Boolean).join(' · ')}</Text>
            </View>
          ))}
        </View>
      )
    case 'projects':
      if (resume.projects.length === 0) return null
      return (
        <View key={key}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'projects')}</Text>
          {resume.projects.map((p) => (
            <View key={p.id} style={{ marginBottom: 2 }} wrap={false}>
              <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{p.name}</Text>
                {p.url ? <Link src={p.url} style={styles.linkInline}>{displayUrl(p.url)}</Link> : null}
              </View>
              {p.description ? <Text style={styles.paragraph}>{p.description}</Text> : null}
              {p.bullets.filter(Boolean).map((b, i) => (
                <View key={i} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>{BULLET}</Text>
                  <Text style={styles.bulletText}>{b}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      )
    case 'certifications':
      if (resume.certifications.length === 0) return null
      return (
        <View key={key} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'certifications')}</Text>
          <Text style={styles.paragraph}>
            {resume.certifications.map((c) => `${c.name}${c.issuer ? ` (${c.issuer})` : ''}${c.date ? ` ${c.date}` : ''}`).join(' · ')}
          </Text>
        </View>
      )
    case 'languages':
      if (resume.languages.length === 0) return null
      return (
        <View key={key} wrap={false}>
          <Text style={styles.sectionTitle}>{labelFor(resume, 'languages')}</Text>
          <Text style={styles.paragraph}>
            {resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}
          </Text>
        </View>
      )
    case 'customSections':
      return resume.customSections.map((s) => (
        <View key={s.id} wrap={false}>
          <Text style={styles.sectionTitle}>{s.title}</Text>
          <Text style={styles.paragraph}>{s.body}</Text>
        </View>
      ))
  }
}

export function CompactPdf({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const meta = pdfMetadata(resume)
  const order = getSectionOrder(resume)
  const font = pdfFontFamily(resume, 'sans')
  const styles = makeStyles(font)
  return (
    <Document title={meta.title} author={meta.author} subject={meta.subject}>
      <Page size="A4" style={styles.page}>
        <Text style={styles.headerName}>{resume.contact.fullName || ' '}</Text>
        {resume.contact.headline ? <Text style={styles.headerLine}>{resume.contact.headline}</Text> : null}
        {contacts.length > 0 && <Text style={styles.headerLine}>{contacts.join(' · ')}</Text>}

        {order.map((key) => renderSection(key, resume, styles))}

        <Text
          style={styles.pageNumber}
          fixed
          render={({ pageNumber, totalPages }) => (totalPages > 1 ? `${pageNumber} / ${totalPages}` : '')}
        />
      </Page>
    </Document>
  )
}

export function CompactPreview({ resume }: { resume: Resume }) {
  const contacts = contactInline(resume.contact)
  const order = getSectionOrder(resume)
  return (
    <div className="bg-white text-slate-900 shadow-sm rounded-md p-8 text-[11px] leading-tight" style={{ fontFamily: previewFontFamily(resume, 'sans') }}>
      <div className="text-base font-bold">{resume.contact.fullName || '\u00a0'}</div>
      {resume.contact.headline && <div className="text-slate-600 text-[10px]">{resume.contact.headline}</div>}
      {contacts.length > 0 && <div className="text-slate-600 text-[10px]">{contacts.join(' · ')}</div>}

      {order.map((key) => {
        switch (key) {
          case 'summary':
            if (!resume.summary) return null
            return <CompactSection key={key} title={labelFor(resume, 'summary')}><p>{resume.summary}</p></CompactSection>
          case 'experience':
            if (resume.experience.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'experience')}>
                {resume.experience.map((e) => (
                  <div key={e.id} className="mb-1.5">
                    <div className="flex justify-between"><div className="font-semibold">{e.role}{e.company ? ` · ${e.company}` : ''}</div><div className="text-slate-500 text-[10px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    {e.location && <div className="text-slate-500 text-[10px]">{e.location}</div>}
                    <ul className="list-disc pl-4">{e.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </CompactSection>
            )
          case 'skills':
            if (resume.skills.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'skills')}>
                {resume.skills.map((g) => <p key={g.id}><b>{g.category}:</b> {g.items.join(', ')}</p>)}
              </CompactSection>
            )
          case 'education':
            if (resume.education.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'education')}>
                {resume.education.map((e) => (
                  <div key={e.id}>
                    <div className="flex justify-between"><div className="font-semibold">{[e.degree, e.field].filter(Boolean).join(', ') || e.institution}</div><div className="text-slate-500 text-[10px]">{joinDateRange(e.startDate, e.endDate)}</div></div>
                    <div className="text-slate-500 text-[10px]">{[e.institution, e.location].filter(Boolean).join(' · ')}</div>
                  </div>
                ))}
              </CompactSection>
            )
          case 'projects':
            if (resume.projects.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'projects')}>
                {resume.projects.map((p) => (
                  <div key={p.id}>
                    <div className="flex justify-between">
                      <div className="font-semibold">{p.name}</div>
                      {p.url && <a className="text-[10px] text-blue-700" href={p.url}>{displayUrl(p.url)}</a>}
                    </div>
                    {p.description && <p>{p.description}</p>}
                    <ul className="list-disc pl-4">{p.bullets.filter(Boolean).map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                ))}
              </CompactSection>
            )
          case 'certifications':
            if (resume.certifications.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'certifications')}>
                <p>{resume.certifications.map((c) => `${c.name}${c.issuer ? ` (${c.issuer})` : ''}${c.date ? ` ${c.date}` : ''}`).join(' · ')}</p>
              </CompactSection>
            )
          case 'languages':
            if (resume.languages.length === 0) return null
            return (
              <CompactSection key={key} title={labelFor(resume, 'languages')}>
                <p>{resume.languages.map((l) => l.proficiency ? `${l.name} (${l.proficiency})` : l.name).join(' · ')}</p>
              </CompactSection>
            )
          case 'customSections':
            return resume.customSections.map((s) => (
              <CompactSection key={s.id} title={s.title}><p className="whitespace-pre-line">{s.body}</p></CompactSection>
            ))
        }
      })}

      {!resume.summary && resume.experience.length === 0 && resume.projects.length === 0 && resume.education.length === 0 && (
        <p className="text-slate-400 italic text-[10px] mt-3">Add content on the left to fill out your resume.</p>
      )}
    </div>
  )
}

function CompactSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5">{title}</div>
      <div>{children}</div>
    </div>
  )
}
